/**
 * Posts TanStack query key factory — keep in sync with posts.query-store usage.
 */
import type { ApiPostSortOrder, InfiniteChannelPostsQueryKey, InfinitePublicPostsQueryKey, PostSortOrder } from '../types/types'

export const mapPostSortOrderToApi = (sortOrder: PostSortOrder): ApiPostSortOrder => (sortOrder === 'oldest' ? 'ASC' : 'DESC')

export const postsQueryKeys = {
  all: (gId?: string) => ['posts', gId] as const,
  single: (postId?: string, groupId?: string, locationId?: string) => ['posts', 'single', postId, groupId, locationId] as const,
  publicPosts: (gId?: string) => ['posts', gId, 'public'] as const,
  infinitePublicPosts: (
    gId?: string,
    locationId?: string,
    limit = 5,
    sortOrder: ApiPostSortOrder = 'DESC'
  ): InfinitePublicPostsQueryKey => ['posts', gId, 'public', locationId, limit, sortOrder, 'infinite'],
  channelPosts: (gId?: string, channelId?: string) => ['posts', gId, 'channel', channelId] as const,
  infiniteChannelPosts: (
    gId?: string,
    channelId?: string,
    locationId?: string,
    limit = 5,
    sortOrder: ApiPostSortOrder = 'DESC'
  ): InfiniteChannelPostsQueryKey => ['posts', gId, 'channel', channelId, locationId, limit, sortOrder, 'infinite'],
  infiniteChannelPinnedPosts: (gId?: string, channelId?: string) => ['posts', gId, 'channel', channelId, 'pinned', 'infinite'] as const,
  infiniteHomePinnedPosts: (gId?: string) => ['posts', gId, 'home-pinned', 'infinite'] as const,
  infiniteUserPosts: (gId?: string, contactId?: string) => ['posts', gId, 'user', contactId, 'infinite'] as const,
} as const

export const hasQueryKeyPrefix = (queryKey: readonly unknown[], prefix: readonly unknown[]): boolean =>
  queryKey.length >= prefix.length && prefix.every((segment, index) => queryKey[index] === segment)

const hasInfiniteSortSuffix = (queryKey: readonly unknown[]): boolean => {
  const sortOrder = queryKey.at(-2)
  return (sortOrder === 'ASC' || sortOrder === 'DESC') && queryKey.at(-1) === 'infinite'
}

export const isInfinitePublicPostsQueryKey = (queryKey: readonly unknown[], groupId: string): boolean => {
  const prefix = postsQueryKeys.publicPosts(groupId)
  return (
    queryKey.length === prefix.length + 4 &&
    hasQueryKeyPrefix(queryKey, prefix) &&
    typeof queryKey[prefix.length + 1] === 'number' &&
    hasInfiniteSortSuffix(queryKey)
  )
}

export const isInfiniteChannelPostsQueryKey = (queryKey: readonly unknown[], groupId: string, channelId?: string): boolean => {
  const prefix = postsQueryKeys.channelPosts(groupId, channelId)
  if (channelId !== undefined) {
    return (
      queryKey.length === prefix.length + 4 &&
      hasQueryKeyPrefix(queryKey, prefix) &&
      typeof queryKey[prefix.length + 1] === 'number' &&
      hasInfiniteSortSuffix(queryKey)
    )
  }

  const groupChannelPrefix = ['posts', groupId, 'channel'] as const
  return (
    queryKey.length === groupChannelPrefix.length + 5 &&
    hasQueryKeyPrefix(queryKey, groupChannelPrefix) &&
    typeof queryKey[groupChannelPrefix.length] === 'string' &&
    typeof queryKey[groupChannelPrefix.length + 2] === 'number' &&
    hasInfiniteSortSuffix(queryKey)
  )
}

const isSortablePostsInfiniteQueryKey = (
  queryKey: readonly unknown[]
): queryKey is InfinitePublicPostsQueryKey | InfiniteChannelPostsQueryKey => {
  const isPublicKey = queryKey.length === 7 && queryKey[0] === 'posts' && queryKey[2] === 'public' && typeof queryKey[4] === 'number'
  const isChannelKey = queryKey.length === 8 && queryKey[0] === 'posts' && queryKey[2] === 'channel' && typeof queryKey[5] === 'number'
  return (isPublicKey || isChannelKey) && hasInfiniteSortSuffix(queryKey)
}

export const getPostsSortChangePlaceholderData = <T>(
  previousData: T | undefined,
  previousQueryKey: readonly unknown[] | undefined,
  currentQueryKey: InfinitePublicPostsQueryKey | InfiniteChannelPostsQueryKey
): T | undefined => {
  if (previousData === undefined || !previousQueryKey || !isSortablePostsInfiniteQueryKey(previousQueryKey)) return undefined
  if (previousQueryKey.length !== currentQueryKey.length) return undefined

  const sortIndex = currentQueryKey.length - 2
  if (previousQueryKey[sortIndex] === currentQueryKey[sortIndex]) return undefined

  const hasSameNonSortSegments = currentQueryKey.every((segment, index) => index === sortIndex || previousQueryKey[index] === segment)
  return hasSameNonSortSegments ? previousData : undefined
}

export const isInfiniteChannelPinnedPostsQueryKey = (queryKey: readonly unknown[], groupId: string): boolean => {
  const groupChannelPrefix = ['posts', groupId, 'channel'] as const
  return (
    queryKey.length === groupChannelPrefix.length + 3 &&
    hasQueryKeyPrefix(queryKey, groupChannelPrefix) &&
    typeof queryKey[groupChannelPrefix.length] === 'string' &&
    queryKey.at(-2) === 'pinned' &&
    queryKey.at(-1) === 'infinite'
  )
}

export const isInfiniteHomePinnedPostsQueryKey = (queryKey: readonly unknown[], groupId: string): boolean =>
  queryKey.length === postsQueryKeys.infiniteHomePinnedPosts(groupId).length &&
  hasQueryKeyPrefix(queryKey, postsQueryKeys.infiniteHomePinnedPosts(groupId))

export const isInfiniteUserPostsQueryKey = (queryKey: readonly unknown[], groupId: string): boolean => {
  const prefix = ['posts', groupId, 'user'] as const
  return queryKey.length === prefix.length + 2 && hasQueryKeyPrefix(queryKey, prefix) && queryKey.at(-1) === 'infinite'
}

export const isSinglePostQueryKeyForGroup = (queryKey: readonly unknown[], groupId: string): boolean =>
  queryKey.length === 5 && hasQueryKeyPrefix(queryKey, ['posts', 'single']) && queryKey[3] === groupId

/**
 * Polls TanStack query key factory — keep in sync with polls.query-store usage.
 *
 * Poll-scoped keys live under the `'poll'` namespace (singular) so they can be
 * invalidated independently of the `'posts'` cache. The post-embedded
 * `poll` payload still rides along with `postsQueryKeys.*` and is patched in
 * place by the polls query store.
 */
export const pollsQueryKeys = {
  all: (pollId?: string) => ['poll', pollId] as const,
  voters: (pollId?: string, optionId?: string, groupId?: string, locationId?: string) =>
    ['poll', pollId, 'voters', optionId, groupId, locationId] as const,
} as const
